import React, { useCallback, useEffect, useState } from "react";
import { Download, RefreshCw } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { CohortMatch } from "@/components/admin/CohortMatch";

const STATUSES = [
  ["free", "Free"],
  ["pionier", "Pionier"],
  ["trial", "Trial"],
  ["abgelaufen", "Abgelaufen"],
];

const SECTIONS = [
  ["registrierung", "Tage seit Registrierung", [["0_14", "0–14"], ["15_60", "15–60"], ["61_180", "61–180"], ["aelter", "älter"], ["nicht_erfasst", "nicht erfasst"]]],
  ["quelle", "Quelle der Registrierung", [["quick_test", "Quick-Test"], ["einladung", "Einladung"], ["challenge", "Challenge"], ["email_link", "E-Mail-Link"], ["paid_ad", "Paid Ad"], ["direkt", "direkt"]]],
  ["letzte_aktivitaet", "Letzte Aktivität (Tage)", [["7", "≤ 7"], ["30", "8–30"], ["90", "31–90"], ["aelter", "älter"], ["nie", "nie"], ["nicht_erfasst", "nicht erfasst"]]],
  ["nutzungen_verteilung", "Nutzungen pro Konto", [["0", "0"], ["1", "1"], ["2_5", "2–5"], ["6_20", "6–20"], ["21_plus", "21+"]]],
  ["quick_test_abgeschlossen", "Quick-Test abgeschlossen", [["ja", "ja"], ["nein", "nein"]]],
  ["fokustyp_gesetzt", "Fokustyp gesetzt", [["ja", "ja"], ["nein", "nein"]]],
  ["fokustyp_verteilung", "Fokustyp", [["katze", "Katze"], ["eule", "Eule"], ["hund", "Hund"], ["biene", "Biene"], ["keiner", "keiner"]]],
  ["doi_bestaetigt", "Double-Opt-in bestätigt", [["ja", "ja"], ["nein", "nein"]]],
];

const th = "px-3 py-2 text-left text-xs font-bold uppercase tracking-wider text-slate-500";
const td = "px-3 py-2 text-sm text-slate-800 tabular-nums";

const CohortTable = ({ id, dataKey, title, rows, status }) => (
  <section className="rounded-xl bg-white border border-slate-200 overflow-x-auto" data-testid={`kohorten-table-${id}`}>
    <h2 className="px-3 pt-3 pb-1 text-sm font-bold text-slate-900">{title}</h2>
    <table className="w-full">
      <thead>
        <tr className="border-b border-slate-100">
          <th className={th}>Gruppe</th>
          {STATUSES.map(([k, l]) => <th key={k} className={th}>{l}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map(([rk, rl]) => (
          <tr key={rk} className="border-b border-slate-50 last:border-0">
            <td className={`${td} text-slate-600`}>{rl}</td>
            {STATUSES.map(([k]) => (
              <td key={k} className={td} data-testid={`kohorten-${id}-${rk}-${k}`}>{status[k]?.[dataKey]?.[rk] ?? 0}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </section>
);

const CohortTables = ({ status, prefix = "" }) => (
  <>
    <SummaryTable status={status} prefix={prefix} />
    {SECTIONS.map(([id, title, rows]) => <CohortTable key={id} id={`${prefix}${id}`} dataKey={id} title={title} rows={rows} status={status} />)}
  </>
);

const SummaryTable = ({ status, prefix = "" }) => (
  <section className="rounded-xl bg-white border border-slate-200 overflow-x-auto" data-testid={`kohorten-table-${prefix}summary`}>
    <h2 className="px-3 pt-3 pb-1 text-sm font-bold text-slate-900">Anzahl pro Status</h2>
    <table className="w-full">
      <thead>
        <tr className="border-b border-slate-100">
          <th className={th}>Kennzahl</th>
          {STATUSES.map(([k, l]) => <th key={k} className={th}>{l}</th>)}
        </tr>
      </thead>
      <tbody>
        {[["anzahl", "Konten"], ["inaktiv", "davon inaktiv (> 90 Tage)"], ["nutzungen_mittelwert", "Nutzungen Mittelwert"], ["nutzungen_median", "Nutzungen Median"]].map(([rk, rl]) => (
          <tr key={rk} className="border-b border-slate-50 last:border-0">
            <td className={`${td} text-slate-600`}>{rl}</td>
            {STATUSES.map(([k]) => (
              <td key={k} className={td} data-testid={`kohorten-${prefix}summary-${rk}-${k}`}>{status[k]?.[rk] ?? 0}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </section>
);

const ExcludedBox = ({ data }) => (
  <section className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700 space-y-1" data-testid="kohorten-excluded">
    <p className="font-bold text-slate-900">Ausgeschlossen / nicht erfasst</p>
    <p>Testkonten: <span data-testid="kohorten-excluded-test">{data.ausgeschlossen.testkonten}</span> · Bezahlte Konten: <span data-testid="kohorten-excluded-paid">{data.ausgeschlossen.bezahlt}</span> (davon Pionier: {data.ausgeschlossen.bezahlt_davon_pionier})</p>
    <p>Status nicht zuordenbar: <span data-testid="kohorten-unmapped">{data.nicht_erfasst.status_nicht_zuordenbar}</span></p>
    <ul className="pl-4 list-disc text-xs text-slate-500">
      {Object.entries(data.nicht_erfasst.status_gruende).map(([k, v]) => <li key={k}>{k}: {v}</li>)}
    </ul>
    <p className="text-xs text-slate-500">„Letzte Aktivität“ = letzte Nutzung eines Moduls (die letzte Anmeldung wird kaum gespeichert). „Nutzungen“ = Durchläufe über alle Module.</p>
  </section>
);

const downloadJson = (data) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `kohorten-report-${data.generated_at.slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(a.href);
};

export default function AdminKohorten() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/admin/user-cohort-report");
      setData(r.data);
    } catch (e) {
      setError(e?.response?.status === 403 ? "Nur für Admin-Konten." : "Auswertung konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminLayout>
      <div data-testid="admin-kohorten-page" className="space-y-5">
        <header className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[#4563BB]">Admin · Kohorten</p>
            <h1 className="mt-1 text-2xl sm:text-3xl font-extrabold text-slate-900">Free, Pionier, Trial, Abgelaufen</h1>
            <p className="mt-1 text-sm text-slate-500">
              Aggregiert, ohne Namen, Mails oder Zahlungsdaten · {data ? `${data.gesamt_gezaehlt} Konten gezählt · Stand ${new Date(data.generated_at).toLocaleString("de-DE")}` : ""}
            </p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={load} disabled={loading} data-testid="kohorten-refresh-btn" className="inline-flex items-center gap-2 rounded-lg bg-[#4563BB] hover:opacity-90 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Aktualisieren
            </button>
            <button type="button" onClick={() => data && downloadJson(data)} disabled={!data} data-testid="kohorten-export-json-btn" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 text-sm font-semibold px-4 py-2 disabled:opacity-50">
              <Download className="w-4 h-4" /> JSON-Export
            </button>
          </div>
        </header>
        {error && <p className="text-sm text-red-600" data-testid="kohorten-error">{error}</p>}
        <CohortMatch renderTables={(st) => <CohortTables status={st} prefix="match-" />} />
        {data && (
          <>
            <CohortTables status={data.status} />
            <ExcludedBox data={data} />
          </>
        )}
      </div>
    </AdminLayout>
  );
}
